import {
  BatchSetting,
  ProductChangeover,
  ProductSetting,
} from 'Features/Projects/Scenarios/EditSettings/LineSettings/LineSettings.types'
import { createContext, Dispatch, useContext } from 'react'
import { max, min, round } from 'lodash'
import { ReplenishmentSiteSku } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.types'
import { Scenario } from 'Features/Projects/Scenarios/Scenarios.types'
import {
  Status,
  PreferredMethod,
} from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import { cycle_round } from 'Features/Projects/Scenarios/EditSettings/utils'

export type BaseSourceSiteWorkCentre = {
  id: number
  baseSourceSiteKey: number
  baseSourceSiteCode: string
  workcentreCode: string
  workCentreKey: number
  workcentreCapacity: number
  headSpaceBuffer: number
  skuGroupCount: number
  preferredTemplate: PreferredMethod
  unconstrainedStatus: Status
  constrainedStatus: Status
  costOptimizedStatus: Status
  carbonCostStatus: Status
  campaignStatus: Status
}

export type SettingsState = {
  projectId: string
  scenarioId: string
  // productSettings: ProductSetting[]
  // batchSettings: BatchSetting[]
  // productChangeovers: ProductChangeover[]
  // baseSourceSiteWorkCentre: BaseSourceSiteWorkCentre[]
  selectedBaseSourceSite: string
  selectedWorkCentre: string
  //replenishmentSiteSkus: ReplenishmentSiteSku[]
  scenarios: Scenario[]
  selectedScenario: string
}

type SettingsStateArg = {
  batchSettings?: BatchSetting[]
}
export const get_batch_cycle = (
  batch_grouping_code: ProductSetting['batchGroupingCode'],
  settingsState: SettingsStateArg
): BatchSetting['minBatchCycleInput'] | undefined => {
  const batchSetting = settingsState.batchSettings?.find(
    (batchSetting: BatchSetting) =>
      batchSetting.batchGroupingCode === batch_grouping_code
  )
  return batchSetting?.minBatchCycleInput
}

export const batch_adjusted_cycle_calc = (
  row: ProductSetting,
  state: SettingsStateArg
): number => {
  const batch_cycle = get_batch_cycle(row.batchGroupingCode, state)

  if (
    row.skuSalesRate > 0 &&
    row.minimumProductionOrderQuantity / row.skuSalesRate > 8
  ) {
    return batch_cycle ?? 8
  }
  return max([batch_cycle, row.initialCycle]) ?? 8
}

export const min_cycle_input_calc = (
  row: ProductSetting,
  state: SettingsStateArg
) => {
  return (
    min([
      max([batch_adjusted_cycle_calc(row, state), row.minCycleOverride]),
      row.maxCycleOverride,
    ]) ?? 1
  )
}

export const replenishment_type_calc = (row: ProductSetting): 'FOC' | 'FOQ' => {
  if (row.skuSalesRate === 0) {
    return 'FOQ'
  }

  if (
    row.minCycleInput <
    row.minimumProductionOrderQuantity / row.skuSalesRate
  ) {
    return 'FOQ'
  } else {
    return 'FOC'
  }
}

export const rep_type = (
  natural_cycle: number,
  min_cycle: number
): 'FOC' | 'FOQ' => {
  if (natural_cycle === 0) {
    return 'FOQ'
  }

  if (min_cycle < natural_cycle) {
    return 'FOQ'
  } else {
    return 'FOC'
  }
}

export type Action =
  | { type: 'UPDATE_PROJECT_ID'; payload: string }
  | { type: 'UPDATE_SCENARIO_ID'; payload: string }
  | { type: 'UPDATE_SKU_SALES_RATE'; payload: number }
  | { type: 'UPDATE_SKU_MIN_CYCLE'; payload: number }
  | { type: 'UPDATE_SKU_MAX_CYCLE'; payload: number }
  | { type: 'UPDATE_SKU_RUN_RATE' }
  | { type: 'PRODUCT_SETTINGS_FETCH'; payload: ProductSetting[] }
  | { type: 'PRODUCT_CHANGEOVERS_FETCH'; payload: ProductChangeover[] }
  | { type: 'BATCH_SETTINGS_FETCH'; payload: BatchSetting[] }
  | { type: 'BASE_SOURCE_SITE_FETCH'; payload: BaseSourceSiteWorkCentre[] }
  | { type: 'REPLENISHMENT_SITE_SKUS_FETCH'; payload: ReplenishmentSiteSku[] }
  | { type: 'PROJECT_DETAILS_FETCH'; payload: Scenario[] }
  | { type: 'UPDATE_SELECTED_SCENARIO'; payload: string }
  | { type: 'UPDATE_BASE_SOURCE_SITE'; payload: string }
  | { type: 'UPDATE_WORKCENTRE'; payload: string }
  | { type: 'RESET_DROPDOWN_OPTIONS' }
  | { type: 'UPDATE_STATE'; payload: SettingsState }
  | { type: 'SET_SKU_CYCLES_TO_CURRENT' }

export const settingsReducer = (
  state: SettingsState,
  action: Action
): SettingsState => {
  switch (action.type) {
    case 'UPDATE_STATE':
      return action.payload
    case 'UPDATE_PROJECT_ID':
      return {
        ...state,
        projectId: action.payload,
      }
    case 'UPDATE_SCENARIO_ID':
      return {
        ...state,
        scenarioId: action.payload,
      }

    case 'PROJECT_DETAILS_FETCH':
      return {
        ...state,
        scenarios: action.payload,
      }
    case 'UPDATE_SELECTED_SCENARIO':
      return {
        ...state,
        selectedScenario: action.payload,
      }
    case 'UPDATE_BASE_SOURCE_SITE':
      return {
        ...state,
        selectedBaseSourceSite: action.payload,
        selectedWorkCentre: 'All',
      }
    case 'UPDATE_WORKCENTRE':
      return {
        ...state,
        selectedWorkCentre: action.payload,
      }
    case 'RESET_DROPDOWN_OPTIONS':
      return {
        ...state,
        selectedWorkCentre: 'All',
        selectedBaseSourceSite: 'All',
      }

    // case 'SET_SKU_CYCLES_TO_CURRENT':
    //   return {
    //     ...state,
    //     productSettings: state.productSettings.map((item: ProductSetting) => {
    //       return {
    //         ...item,
    //         minCycleInput: item.currentCycleTime,
    //         maxCycleInput: item.currentCycleTime,
    //       }
    //     }),
    //   }
    default:
      return state
  }
}

export type SettingsContextType = {
  state: SettingsState
  dispatch: Dispatch<Action>
}

export const SettingsContext = createContext<SettingsContextType | undefined>(
  undefined
)

export const useSettingsContext = (): SettingsContextType => {
  const context = useContext(SettingsContext)
  if (!context) throw new Error('Must be within Settings Provider')
  return context
}

export const productRowCalculations = (
  state: SettingsStateArg,
  row: ProductSetting
): ProductSetting => {
  const natural_cycle = round(
    row.minimumProductionOrderQuantity / row.allocatedSalesRate,
    2
  )
  const new_min_cycle_input = min_cycle_input_calc(row, state)
  const replenishment_type = rep_type(natural_cycle, new_min_cycle_input)

  return {
    ...row,
    naturalCycle: natural_cycle,
    initialCycle: cycle_round(
      row.minimumProductionOrderQuantity / row.allocatedSalesRate
    ),
    minCycleInput: new_min_cycle_input,
    maxCycleInput: row.maxCycleOverride,
    replenishmentTypeCode: replenishment_type,
    replenishmentTypeKey: replenishment_type === 'FOQ' ? 1 : 2,
    isIncluded: row.allocatedSalesRate > 0,
  }
}

export type ProcessRowUpdate = (newRow: ProductSetting) => ProductSetting

export const processRowUpdate = (
  batchData: BatchSetting[]
): ProcessRowUpdate => {
  return (newRow: ProductSetting) => {
    return productRowCalculations({ batchSettings: batchData }, newRow)
  }
}

export const batchRowCalculations = (row: BatchSetting): BatchSetting => {
  const replenishment_type_calc = (row: BatchSetting): 'FOC' | 'FOQ' => {
    if (row.salesRate === 0) {
      return 'FOQ'
    }

    if (row.maxBatchCycleInput < row.minimumBatchSize / row.salesRate) {
      return 'FOQ'
    } else {
      return 'FOC'
    }
  }

  const min_cycle_input_calc = (row: BatchSetting) => {
    return (
      min([
        max([row.initialBatchCycle, row.minBatchCycleOverride]),
        row.maxBatchCycleOverride,
      ]) ?? 1
    )
  }

  return {
    ...row,
    naturalCycle: round(row.minimumBatchSize / row.salesRate, 2),
    initialBatchCycle: cycle_round(row.minimumBatchSize / row.salesRate),
    minBatchCycleInput: min_cycle_input_calc(row),
    maxBatchCycleInput: row.maxBatchCycleOverride,
    replenishmentTypeCode: replenishment_type_calc(row),
    replenishmentTypeKey: replenishment_type_calc(row) === 'FOQ' ? 1 : 2,
    isIncluded: row.salesRate > 0,
  }
}
